import React, { useState } from 'react';
import {
    getConfirmedAppointmentsByUser,
    sendCancellationCode,
    cancelLibraryAppointmentByCode
} from '../../api/libraryAppointmentService';

import '../../styles/pages/libraryAppointment.scss';

const AppointmentCancellation = () => {
    const [identityNumber, setIdentityNumber] = useState('');
    const [appointments, setAppointments] = useState([]);
    const [searched, setSearched] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');

    // OTP Dialog State
    const [showOtpDialog, setShowOtpDialog] = useState(false);
    const [selectedAppointment, setSelectedAppointment] = useState(null);
    const [verificationCode, setVerificationCode] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const handleSearch = async (e) => {
        e.preventDefault();
        setError('');
        setMessage('');

        if (!identityNumber || identityNumber.length !== 11) {
            setError('Lütfen geçerli bir TC Kimlik Numarası giriniz.');
            return;
        }

        setLoading(true);
        setSearched(false);

        try {
            const result = await getConfirmedAppointmentsByUser(identityNumber);

            if (result?.isSuccess) {
                setAppointments(result.data || []);
                setSearched(true);
                if ((result.data || []).length === 0) {
                    setMessage('Bu kimlik numarasına ait aktif (onaylanmış) randevu bulunamadı.');
                }
            } else {
                setError(result?.resultMessage || 'Randevular yüklenirken bir hata oluştu.');
            }
        } catch (err) {
            setError('Randevular yüklenirken bir hata oluştu.');
        } finally {
            setLoading(false);
        }
    };

    const handleRequestCancel = async (appointment) => {
        setSelectedAppointment(appointment);
        setError('');
        setMessage('');
        setSubmitting(true);

        try {
            const result = await sendCancellationCode(appointment.id);

            if (result?.isSuccess) {
                setMessage(result.resultMessage || 'Doğrulama kodu e-posta adresinize gönderildi.');
                setVerificationCode('');
                setShowOtpDialog(true);
            } else {
                setError(result?.resultMessage || 'Kod gönderilirken bir hata oluştu.');
            }
        } catch (err) {
            setError('Kod gönderilirken bir hata oluştu.');
        } finally {
            setSubmitting(false);
        }
    };

    const handleConfirmCancel = async (e) => {
        e.preventDefault();
        setError('');
        setMessage('');

        if (!verificationCode || verificationCode.length < 6) {
            setError('Lütfen doğrulama kodunu giriniz.');
            return;
        }

        setSubmitting(true);

        try {
            const result = await cancelLibraryAppointmentByCode(
                selectedAppointment.id,
                verificationCode
            );

            if (result?.isSuccess) {
                setMessage(result.resultMessage || 'Randevunuz başarıyla iptal edildi.');
                setShowOtpDialog(false);
                // Remove cancelled appointment from list
                const newList = appointments.filter(x => x.id !== selectedAppointment.id);
                setAppointments(newList);
                setSelectedAppointment(null);
                setVerificationCode('');
            } else {
                setError(result?.resultMessage || 'İptal işlemi başarısız oldu.');
            }
        } catch (err) {
            setError('İptal işlemi başarısız oldu.');
        } finally {
            setSubmitting(false);
        }
    };

    const handleCloseDialog = () => {
        setShowOtpDialog(false);
        setVerificationCode('');
        setError('');
    };

    return (
        <div className="library-appointment-page">
            <div className="container">
                <header className="la-header">
                    <h1>Kütüphane Randevu İptali</h1>
                    <p>
                        Randevularınızı görüntülemek ve iptal etmek için lütfen TC kimlik numaranızı giriniz.
                    </p>
                </header>

                {error && <div className="la-alert la-alert-error">{error}</div>}
                {message && <div className="la-alert la-alert-info">{message}</div>}

                <form className="la-form" onSubmit={handleSearch}>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
                        <div className="la-field" style={{ maxWidth: '400px', width: '100%' }}>
                            <label htmlFor="identityNumber">TC Kimlik Numarası *</label>
                            <input
                                id="identityNumber"
                                name="identityNumber"
                                type="text"
                                value={identityNumber}
                                onChange={(e) => {
                                    const val = e.target.value.replace(/\D/g, '');
                                    if (val.length <= 11) setIdentityNumber(val);
                                }}
                                maxLength={11}
                                required
                                placeholder="11 haneli TC kimlik numaranız"
                            />
                        </div>

                        <button
                            type="submit"
                            className="la-submit"
                            disabled={loading}
                            style={{ marginTop: '1.5rem' }}
                        >
                            {loading ? 'Sorgulanıyor…' : 'Randevularımı Göster'}
                        </button>
                    </div>
                </form>

                {appointments.length > 0 && (
                    <div style={{ marginTop: '2rem' }}>
                        <h3 style={{ marginBottom: '1rem', textAlign: 'center' }}>Aktif Randevularınız</h3>
                        <div className="la-appointments-list">
                            {appointments.map((apt) => (
                                <div key={apt.id} className="la-appointment-card">
                                    <div className="la-appointment-info">
                                        <div className="la-appointment-date">
                                            <strong>{new Date(apt.appointmentDate).toLocaleDateString('tr-TR', {
                                                day: 'numeric',
                                                month: 'long',
                                                year: 'numeric'
                                            })}</strong>
                                        </div>
                                        <div className="la-appointment-details">
                                            <span>Ad Soyad: <strong>{apt.maskedFirstName} {apt.maskedLastName}</strong></span>
                                        </div>
                                        <div className="la-appointment-details">
                                            <span>Saat: {apt.slotStartTime?.substring(0, 5)} - {apt.slotEndTime?.substring(0, 5)}</span>
                                            <span>Masa No: <strong>{apt.seatNumber}</strong></span>
                                        </div>
                                        <div className="la-appointment-email">
                                            <small>E-posta: {apt.maskedEmail}</small>
                                        </div>
                                    </div>
                                    <div className="la-appointment-actions">
                                        <button
                                            type="button"
                                            className="la-btn-cancel"
                                            onClick={() => handleRequestCancel(apt)}
                                            disabled={submitting}
                                        >
                                            İptal Et
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* OTP Dialog */}
                {showOtpDialog && selectedAppointment && (
                    <div className="la-modal-overlay" onClick={handleCloseDialog}>
                        <div className="la-modal-content" onClick={(e) => e.stopPropagation()}>
                            <div className="la-modal-header">
                                <h3>Doğrulama Kodu</h3>
                                <button
                                    type="button"
                                    className="la-modal-close"
                                    onClick={handleCloseDialog}
                                    aria-label="Kapat"
                                >
                                    ×
                                </button>
                            </div>
                            <form onSubmit={handleConfirmCancel}>
                                <div className="la-modal-body">
                                    <p style={{ marginBottom: '1rem' }}>
                                        Lütfen <strong>{selectedAppointment.maskedEmail}</strong> adresine gönderilen
                                        6 haneli doğrulama kodunu giriniz.
                                    </p>
                                    <div className="la-field">
                                        <label htmlFor="verificationCode">Doğrulama Kodu *</label>
                                        <input
                                            id="verificationCode"
                                            name="verificationCode"
                                            type="text"
                                            value={verificationCode}
                                            onChange={(e) => {
                                                const val = e.target.value.replace(/\D/g, '');
                                                if (val.length <= 6) setVerificationCode(val);
                                            }}
                                            maxLength={6}
                                            required
                                            autoFocus
                                            placeholder="6 haneli kod"
                                        />
                                    </div>
                                </div>
                                <div className="la-modal-footer">
                                    <button
                                        type="button"
                                        className="la-btn-secondary"
                                        onClick={handleCloseDialog}
                                        disabled={submitting}
                                    >
                                        Kapat
                                    </button>
                                    <button
                                        type="submit"
                                        className="la-btn-danger"
                                        disabled={submitting}
                                    >
                                        {submitting ? 'İşleniyor…' : 'Onayla ve İptal Et'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AppointmentCancellation;
